import { useEffect, useState } from 'react';

import { getCurrentCognitoUser, requireMFASetup } from '../../utils/helper';
import { requireMFAValidation } from '../../utils/helper';
import { CognitoUser } from '../../types/user';

import { useClient } from './';

type AuthStates = {
  status:
    | 'authenticated'
    | 'notMFAValidated'
    | 'requireMFASetup'
    | 'unauthenticated'
    | null;
  user: CognitoUser | null;
};

export function useAuthenticatedUser({
  checkMFAValidation = false,
  bypassCache = false,
}) {
  const { clientId, mfaToken } = useClient();
  const [authenticatedUser, setAuthenticatedUser] = useState<AuthStates>({
    status: null,
    user: null,
  });

  useEffect(() => {
    const setAuth = async () => {
      try {
        const cognitoUser = await getCurrentCognitoUser();

        if (checkMFAValidation) {
          if (requireMFASetup(cognitoUser.attributes))
            setAuthenticatedUser({ status: 'requireMFASetup', user: null });
          else if (await requireMFAValidation(mfaToken, clientId))
            setAuthenticatedUser({ status: 'notMFAValidated', user: null });
          else
            setAuthenticatedUser({
              status: 'authenticated',
              user: cognitoUser,
            });
        } else
          setAuthenticatedUser({ status: 'authenticated', user: cognitoUser });
      } catch (e: any) {
        setAuthenticatedUser({ status: 'unauthenticated', user: null });
      }
    };

    if (clientId) {
      setAuth();
    }
  }, [clientId, checkMFAValidation, bypassCache, mfaToken]);

  const refreshAuthenticatedUser = async () => {
    let user;
    try {
      user = await getCurrentCognitoUser();
      setAuthenticatedUser({ status: 'authenticated', user });
    } catch (e: any) {}
    return user;
  };

  return {
    authenticationStatus: authenticatedUser.status,
    authenticatedUser: authenticatedUser?.user
      ? {
          ...authenticatedUser?.user,
          attributes: {
            ...(authenticatedUser?.user?.attributes || {}),
            given_name: authenticatedUser?.user?.attributes?.given_name || '',
            family_name: authenticatedUser?.user?.attributes?.family_name || '',
          },
        }
      : authenticatedUser?.user,
    refreshAuthenticatedUser,
    setAuthenticatedUser,
  } as const;
}
